import classNames from 'classnames'
import { useLayoutEffect, useCallback, useState } from 'react'
import { setupCard } from '../../libs/tapPay'
import styles from './tappay.module.css'

const CARD_FIELDS = {
  number: {
    element: '#tpfield-card-number',
    placeholder: 'xxxx xxxx xxxx xxxx',
  },
  expirationDate: {
    element: '#tpfield-card-expiration-date',
    placeholder: 'MM / YY',
  },
  ccv: {
    element: '#tpfield-card-ccv',
    placeholder: '',
  },
}

const CARD_STYLES = {
  input: {
    color: '#9b9b9b',
    'font-size': '16px',
  },
  ':focus': {
    color: '#4a4a4a',
  },
  '.valid': {
    color: '#00ad43',
  },
  '.invalid': {
    color: '#ff5050',
  },
}

enum InputStatusCode {
  ERROR = 2,
  SUCCESS = 0,
  NORMAL = 1, // or 3
}

type CardType = 'unknown' | 'mastercard' | 'visa' | 'jcb' | 'unionpay'

type TapPayUpdate = {
  canGetPrime: boolean
  cardType: CardType
  status: {
    number: InputStatusCode
    expiry: InputStatusCode
    ccv: InputStatusCode
  }
}

const CARD_TYPES = [
  { name: 'mastercard', icon: '/images/payment/mastercard.svg' },
  { name: 'visa', icon: '/images/payment/visa.svg' },
  { name: 'jcb', icon: '/images/payment/jcb.svg' },
  { name: 'unionpay', icon: '/images/payment/union_pay.png' },
]

export default function TapPayFormFields(props: {
  submitted: boolean
  setCanSubmit: (value: boolean) => void
}) {
  const { submitted, setCanSubmit } = props

  const [inputStatus, setInputStatus] = useState({
    number: InputStatusCode.ERROR,
    expiry: InputStatusCode.ERROR,
    ccv: InputStatusCode.ERROR,
  })

  const [cardType, setCardType] = useState<CardType>('unknown')

  const onUpdateCallback = useCallback(
    (update: TapPayUpdate) => {
      setCanSubmit(update.canGetPrime)
      setCardType(update.cardType)
      setInputStatus(update.status)
    },
    [setCanSubmit, setInputStatus, setCardType],
  )

  useLayoutEffect(() => {
    try {
      setupCard({
        fields: CARD_FIELDS,
        styles: CARD_STYLES,
        onUpdate: onUpdateCallback,
      })
    } catch (err) {
      console.error('setupCard error', err)
    }
  }, [onUpdateCallback])

  return (
    <div className="container pt-l pb-m">
      <div className="w-full">
        <label className="babu-text-paragraph-bold mb-xxs">信用卡號碼</label>
        <div
          className={classNames(styles.tpfield, 'card-number', {
            error: submitted && inputStatus.number === InputStatusCode.ERROR,
            success: inputStatus.number === InputStatusCode.SUCCESS,
          })}
          id="tpfield-card-number"
        />
        {CARD_TYPES.filter(
          TYPE => cardType === 'unknown' || cardType === TYPE.name,
        ).map(TYPE => (
          <div className="inline-block mr-xxs w-xl h-xl" key={TYPE.name}>
            <img className="w-full" src={TYPE.icon} />
          </div>
        ))}
        {submitted && inputStatus.number === InputStatusCode.ERROR ? (
          <p className="babu-text text-primary900">請輸入正確卡號</p>
        ) : null}
      </div>
      <div className="w-1/2 inline-block pr-xxs">
        <label className="babu-text-paragraph-bold mb-xxs">
          到期日（月/年）
        </label>
        <div
          className={classNames(styles.tpfield, 'card-expiration-date', {
            error: submitted && inputStatus.expiry === InputStatusCode.ERROR,
            success: inputStatus.expiry === InputStatusCode.SUCCESS,
          })}
          id="tpfield-card-expiration-date"></div>
        {submitted && inputStatus.expiry === InputStatusCode.ERROR ? (
          <p className="babu-text text-primary900">請輸入正確卡片效期</p>
        ) : null}
      </div>
      <div className="w-1/2 inline-block pl-xxs">
        <label className="babu-text-paragraph-bold mb-xxs">安全碼</label>
        {/* <Popup
          pinned
          content="在信用卡背面簽名欄或置於其上的最後三位數字號碼"
          on="click"
          trigger={
            <img
              className="icon-info"
              alt="icon-info"
              src="/images/icon-info.svg"
            />
          }
        /> */}
        <div
          className={classNames(styles.tpfield, 'card-ccv', {
            error: submitted && inputStatus.ccv === InputStatusCode.ERROR,
            success: inputStatus.ccv === InputStatusCode.SUCCESS,
          })}
          id="tpfield-card-ccv"></div>
        {submitted && inputStatus.ccv === InputStatusCode.ERROR ? (
          <p className="babu-text text-primary900">請輸入正確安全碼</p>
        ) : null}
      </div>
    </div>
  )
}
